import { useState, useEffect, useRef, useCallback } from 'react';
import threadService from '../services/threadService';

/**
 * useThreadSearch — Debounced search hook for threads + message content.
 * Calls searchThreadsWithContent with 300ms debounce.
 */
export const useThreadSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const data = await threadService.searchThreadsWithContent(query, { limit: 20 });
        setResults(data.threads || []);
      } catch (err) {
        console.error('[useThreadSearch] Search error:', err);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  return { query, setQuery, results, isSearching, clearSearch };
};
